import React from 'react';
import { cn } from '../../utils/cn';
import { getProviderBadgeClass, ProviderColorKey } from '../../utils/providerColors';

type SemanticVariant = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'default';
export type BadgeVariant = SemanticVariant | ProviderColorKey;

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

const SEMANTIC_CLASSES: Record<SemanticVariant, string> = {
  default: 'bg-primary/10 text-primary',
  success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  danger: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  info: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  muted: 'bg-muted text-muted-foreground',
};

const PROVIDER_KEYS: ProviderColorKey[] = ['aws', 'azure', 'gcp'];

function isProviderVariant(variant: BadgeVariant): variant is ProviderColorKey {
  return (PROVIDER_KEYS as string[]).includes(variant);
}

/**
 * Shared status/type/provider pill - replaces the per-page bespoke
 * bg-X-100 text-X-800 literals (getStatusColor/getPriorityColor/getTypeColor/
 * getSeverityColor each defined their own copy of this). Provider variants
 * route through the existing REQ-012-HYBRID getProviderBadgeClass() so this
 * doesn't fork a second provider-color system.
 */
export const Badge: React.FC<BadgeProps> = ({ variant = 'default', className, children, ...props }) => {
  const colorClass = isProviderVariant(variant) ? getProviderBadgeClass(variant) : SEMANTIC_CLASSES[variant];
  return (
    <span
      className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', colorClass, className)}
      {...props}
    >
      {children}
    </span>
  );
};
